import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  useGetAllDiscountsFromChamberMemberIdQuery,
  UserType,
} from "../../../graphql/types";
import "react-toastify/dist/ReactToastify.css";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";
import { useHistory, useLocation } from "react-router";
import useMemberDashboardAccess from "../../common/Hooks/useMemberDashboardAccess";
import Loader from "../../common/Loader/Loader";
import { UserTypeContext } from "../../../UserTypeContext";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf, openMemberInstructionPdf } from "../../common/openInstructionPdf";
import SuccessDialog from "../../common/SuccessDialog";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import FullPageDiscountTable from "../../common/FullPageDiscountTable";

const useStyles = makeStyles(() => ({
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "100px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    "@media (max-width: 768px)": {
      minHeight: "auto",
    },
    "@media (max-width: 600px)": {
      padding: "20px 16px",
    },
  },
  headerTitle: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    margin: "0",
    fontFamily: "DM Sans, sans-serif",
    "@media (max-width: 600px)": {
      fontSize: "22px",
    },
  },
  headerDescription: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    margin: "0",
    fontFamily: "DM Sans, sans-serif",
    lineHeight: "1.5",
    "@media (max-width: 600px)": {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "8px",
    "@media (max-width: 600px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "8px",
      "& > div": {
        width: "100%",
      },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  contentContainer: {
    backgroundColor: theme.palette.primary.main,
    padding: "0 40px 40px 40px",
    "@media (max-width: 600px)": {
      padding: "0 12px 24px 12px",
    },
  },
  content: {
    display: "flex",
    background: "#FFFFFF",
    minHeight: "70vh",
    borderRadius: "16px",
    padding: "30px",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
    },
    flexDirection: "column",
  },
}));

const ChamberMemberListDiscounts = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const location = useLocation();

  const [createSuccessOpen, setCreateSuccessOpen] = useState(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  const bingo = useMemo(() => {
    return (
      location.state || { showToast: undefined, showSuccessDialog: undefined }
    ) as {
      showToast: boolean | undefined;
      showSuccessDialog?: "created" | "deleted";
    };
  }, [location.state]);

  useEffect(() => {
    if (bingo.showSuccessDialog === "deleted") {
      setDeleteSuccessOpen(true);
      const timeoutId = setTimeout(() => setDeleteSuccessOpen(false), 5000);
      return () => clearTimeout(timeoutId);
    }
    if (bingo.showSuccessDialog === "created" || bingo.showToast) {
      setCreateSuccessOpen(true);
      const timeoutId = setTimeout(() => setCreateSuccessOpen(false), 5000);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [bingo.showSuccessDialog, bingo.showToast]);

  const chamberMemberInfo = useGetChamberMemberFromUserIdInAuth();
  const { access: memberAccess } = useMemberDashboardAccess();
  const history = useHistory();
  const { data: discountData, loading } =
    useGetAllDiscountsFromChamberMemberIdQuery({
      variables: { chamberMemberId: chamberMemberInfo?.id || "" },
      skip: !chamberMemberInfo?.id,
      fetchPolicy: "cache-and-network",
    });

  const {
    state: { userType },
  } = useContext(UserTypeContext);

  const rootAdminObj =
    enumStrings.groups[UserType.ChamberMemberAdmin][
      RedirectUri.manageDiscountChamberMemberAdmin
    ];

  const allDiscountData = useMemo(() => {
    return (
      discountData?.getAllDiscountsFromChamberMemberId
        ?.slice()
        .sort((a, b) =>
          (a.title || "") > (b.title || "") ? 1 : -1
        ) || []
    ).map((d) => ({
      ...d,
      title: d.title?.trim() || "",
    }));
  }, [discountData?.getAllDiscountsFromChamberMemberId]);

  return (
    <div style={{ overflowX: "hidden" }}>
      {/* Header Section */}
      <div className={classes.headerSection}>
        <h1 className={classes.headerTitle}>{chamberMemberInfo?.name ? `Manage ${chamberMemberInfo.name} Perks` : rootAdminObj.title}</h1>
        <p className={classes.headerDescription}>{rootAdminObj.description}</p>
        <div className={classes.buttonContainer}>
          {userType === UserType.ChamberMemberAdmin && (
            <VideoTutorialButton
              tutorialUrl="https://share.synthesia.io/embeds/videos/784da11f-0d07-4066-987b-2e92e95713aa"
            />
          )}
          <ExportButton
            customOnClick={() =>
              userType === UserType.ChamberMemberAdmin
                ? openMemberInstructionPdf("Member Dashboard - Manage Company Perks.pdf")
                : openInstructionPdf("Manage Member Perks.pdf")
            }
            icon={
              <InfoOutlinedIcon
                style={{
                  color: theme.palette.primary.main,
                  marginRight: "10px",
                  width: "24px",
                  height: "24px",
                }}
              />
            }
            label={
              enumStrings.manageChamberEngage?.viewInstructions ||
              "VIEW INSTRUCTIONS"
            }
            disablePadding
          />
        </div>
      </div>

      {/* Content Container */}
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          {!discountData || loading ? (
            <Loader />
          ) : (
            <>
              {allDiscountData && allDiscountData.length > 0 ? (
                <FullPageDiscountTable
                  allDiscountData={allDiscountData}
                  userType={UserType.ChamberMemberAdmin}
                  onDeleteSuccess={() => {
                    setDeleteSuccessOpen(true);
                    setTimeout(() => setDeleteSuccessOpen(false), 5000);
                  }}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12, padding: "40px 16px", color: "#0A0A0A" }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E1A731" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11.5 3a2 2 0 00-1.4.6L3.6 10.1a2 2 0 000 2.8l7.5 7.5a2 2 0 002.8 0l6.5-6.5a2 2 0 00.6-1.4V5a2 2 0 00-2-2z" /><circle cx="16" cy="8" r="1.5" /></svg>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>No Perks Yet</div>
                  <div style={{ fontSize: 14, maxWidth: 460, lineHeight: 1.55 }}>You haven't created any perks yet. Create your first perk to promote your company on our Chamber Perks App™ and website directories.</div>
                  {memberAccess?.canCreatePerks !== false && (
                    <button type="button" onClick={() => history.push(RedirectUri.createDiscountChamberMemberAdmin)}
                      style={{ background: "#E1A731", color: "#FFFFFF", border: "none", borderRadius: 8, padding: "11px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                      Create a Perk
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SuccessDialog
        open={createSuccessOpen}
        onClose={() => setCreateSuccessOpen(false)}
        message="Your Perk has been Successfully Created!"
      />
      <SuccessDialog
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        message="Perk Deleted Successfully."
      />
      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
      />
    </div>
  );
};

export default ChamberMemberListDiscounts;
